import { supabase, updateSessionStart } from '../lib/supabase';
import { Profile } from '../lib/supabase';
import {
  authCardHtml, mountAuthScreen, fieldHtml, capsHintHtml, alertHtml, submitHtml, EMAIL_ATTRS,
  bindReveal, bindCaps, bindMarkLight, showAlert, clearAlert, clearAlertOnInput, setBusy, setDone, markState,
  authErrorCopy, takeAuthNotices, deepLinkNotice, greeting, type Notice,
} from './auth/AuthShell';
import { celebrateAuthFx } from './auth/authFx';

export type LoginOptions = { sessionExpired?: boolean };

export class LoginPage {
  private container: HTMLElement;
  private onLoginSuccess: (profile: Profile) => void;
  private onForgotPassword: () => void;
  private opts: LoginOptions;

  // R116 security: no public sign-up (logins are made by an admin on the Users page).
  // R116 #1: the "Welcome home" shell (pages/auth) — same ids, same Supabase sequence.
  constructor(container: HTMLElement, onLoginSuccess: (profile: Profile) => void, onForgotPassword: () => void, opts: LoginOptions = {}) {
    this.container = container;
    this.onLoginSuccess = onLoginSuccess;
    this.onForgotPassword = onForgotPassword;
    this.opts = opts;
  }

  async render(): Promise<void> {
    // R116 security: why the last session was turned away (an unapproved or
    // role-less login) — the hotfix leaves it in sessionStorage; shown until
    // the next page load, never auto-hidden.
    const notices: Notice[] = takeAuthNotices();
    if (this.opts.sessionExpired && !notices.some(n => n.tone === 'ok')) {
      notices.push({ tone: 'info', text: "You've been signed out. Sign in to pick up where you left off.", id: 'auth-expired' });
    }
    const deep = deepLinkNotice();
    if (deep) notices.push(deep);

    const card = mountAuthScreen(this.container, authCardHtml({
      screen: 'login',
      hello: `<b>${greeting()}.</b> Sign in to continue.`,
      notices,
      body: `
        ${alertHtml('login-error')}
        <form id="login-form" novalidate>
          ${fieldHtml({ id: 'login-email', label: 'Email', type: 'email', name: 'email', autocomplete: 'username', placeholder: 'you@company.com', extra: EMAIL_ATTRS })}
          ${fieldHtml({ id: 'login-password', label: 'Password', type: 'password', name: 'password', autocomplete: 'current-password', placeholder: 'Your password', reveal: true, after: capsHintHtml('login-caps') })}
          ${submitHtml('login-submit', 'Sign In')}
        </form>
        <div class="au-links"><a href="#" id="forgot-password" class="au-link">Forgot password?</a></div>`,
      foot: 'Need a login? <b>Ask your admin to add you.</b>',
    }));

    // Type straight away — focus now, no entrance on the fields (Skeptic D3).
    const email = document.getElementById('login-email') as HTMLInputElement | null;
    email?.focus();
    this.bindEvents(card);
  }

  private revertReveal: () => void = () => {};

  private bindEvents(card: HTMLElement): void {
    const email = document.getElementById('login-email') as HTMLInputElement | null;
    const password = document.getElementById('login-password') as HTMLInputElement | null;
    const errorDiv = document.getElementById('login-error');
    this.revertReveal = bindReveal(card);
    bindCaps(password, document.getElementById('login-caps'));
    bindMarkLight(card, [email, password]);
    clearAlertOnInput(errorDiv, [email, password]);

    const form = document.getElementById('login-form');
    if (form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        await this.handleLogin();
      });
    }

    const forgotLink = document.getElementById('forgot-password');
    if (forgotLink) {
      forgotLink.addEventListener('click', (e) => {
        e.preventDefault();
        this.onForgotPassword();
      });
    }
  }

  private async handleLogin(): Promise<void> {
    const emailEl = document.getElementById('login-email') as HTMLInputElement;
    const passwordEl = document.getElementById('login-password') as HTMLInputElement;
    const email = emailEl.value.trim();
    const password = passwordEl.value;
    const errorDiv = document.getElementById('login-error');
    const submitBtn = document.getElementById('login-submit') as HTMLButtonElement;
    const card = this.container.querySelector('.au-card') as HTMLElement | null;
    if (submitBtn?.disabled) return;
    // Password managers only offer to save a type=password field (Skeptic D6).
    this.revertReveal();

    if (!email || !password) {
      showAlert(errorDiv, !email && !password ? 'Enter your email and password.' : !email ? 'Enter your email.' : 'Enter your password.', [!email ? emailEl : null, !password ? passwordEl : null]);
      (!email ? emailEl : passwordEl).focus();
      return;
    }

    clearAlert(errorDiv, [emailEl, passwordEl]);
    setBusy(submitBtn, true, 'Signing in…');
    markState(card, { busy: true });

    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });

      if (error) {
        markState(card, { busy: false });
        setBusy(submitBtn, false, 'Sign In');
        const code = (error as any).code;
        showAlert(errorDiv, authErrorCopy(error, 'login'), code === 'invalid_credentials' ? [passwordEl] : []);
        if (code === 'invalid_credentials') { passwordEl.focus(); passwordEl.select(); }
        return;
      }

      if (data.user) {
        console.log('Login successful for:', email);

        // A placeholder for the success callback only — the page reloads and
        // /api/me supplies the real role. R116 security: the client NEVER
        // writes a role (this used to upsert role 'manager' into profiles).
        const profile: Profile = {
          id: data.user.id,
          email: email,
          full_name: data.user.user_metadata?.full_name || email.split('@')[0],
          role: 'manager',
          is_active: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          avatar_url: null,
          phone: null
        };

        // The welcome is decorative and instant — it never delays the hand-off.
        markState(card, { busy: false, lit: true, open: true });
        setDone(submitBtn, 'Welcome back');
        celebrateAuthFx();

        await updateSessionStart();

        // Call the success callback
        this.onLoginSuccess(profile);
      }
    } catch (err: any) {
      console.error('Login error:', err);
      markState(card, { busy: false });
      setBusy(submitBtn, false, 'Sign In');
      showAlert(errorDiv, authErrorCopy(err, 'login'));
    }
  }
}
